import { useRef, useState } from 'react'
import type { PendingDocumentShare, DocumentSharePdf } from '@/shared/lib/documentShareStore'
import { toFriendlyErrorMessage } from '@/shared/lib/errorMessage'
import { openExternalUrl } from '@/shared/lib/openExternalUrl'
import { downloadPdfFile, pdfBytesToFile, sharePdfFile } from '@/shared/lib/platform/files'
import { loadClinicalDocumentShareText } from '@/shared/lib/shareClinicalDocumentLink'
import { buildDocumentShareText, buildWhatsAppUrl } from '@/shared/lib/whatsappShare'
import { signedDocumentsRepository } from '@/shared/repositories/signedDocuments/signedDocumentsRepository'
import { Button } from '@/shared/ui/Button'
import { CopyIcon, DownloadIcon, LinkIcon, MessageCircleIcon } from '@/shared/ui/icons'
import { useToast } from '@/shared/ui/toast'
import { trackProductEvent } from '@/shared/lib/posthog/trackProductEvent'

export interface ShareOptionsProps {
  document: PendingDocumentShare
}

type ShareAction = 'pdf' | 'whatsapp' | 'copy' | 'download'

const SHARE_CHANNEL: Record<ShareAction, string> = { pdf: 'whatsapp_pdf', whatsapp: 'whatsapp_link', copy: 'copiar', download: 'baixar' }

/** SHARE-03..08 — mesmas opções na folha de compartilhar e depois de salvar/assinar. */
export function ShareOptions({ document }: ShareOptionsProps) {
  const [busy, setBusy] = useState<ShareAction | null>(null)
  const busyRef = useRef(false)
  const preparedPdf = useRef<{ file: File; text: string; signed: boolean; documentId: string } | null>(null)
  const [pdfReady, setPdfReady] = useState(false)
  const toast = useToast()
  const label = buildDocumentShareText(document)

  const loadPdf = async (): Promise<DocumentSharePdf> => {
    // Reconsulta: a assinatura pode ter terminado com esta folha ainda aberta.
    const signed = await signedDocumentsRepository.getLatestForClinicalDocument(document.documentId)
    if (!signed && !document.signed) {
      if (!document.loadPdf) throw new Error('O PDF deste documento não está disponível. Tente novamente.')
      return document.loadPdf()
    }
    const path = signed?.signedPdfStoragePath
    if (!path) throw new Error('O PDF assinado não está disponível. Tente novamente.')
    const url = await signedDocumentsRepository.createSignedPdfDownloadUrl(path)
    const response = await fetch(url)
    if (!response.ok) throw new Error('Não foi possível baixar o PDF assinado. Tente novamente.')
    return {
      bytes: new Uint8Array(await response.arrayBuffer()),
      filename: document.filename ?? `documento-${document.documentId}.pdf`,
    }
  }

  const run = async (action: ShareAction) => {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(action)
    // Captura antes das operações assíncronas para manter a intenção do toque.
    const desktop = window.matchMedia?.('(min-width: 640px)').matches === true
    let failed = false
    try {
      if (action === 'pdf' && !desktop && preparedPdf.current?.signed === document.signed && preparedPdf.current.documentId === document.documentId) {
        const { file, text } = preparedPdf.current
        const result = await sharePdfFile(file, label, text)
        if (result === 'unavailable') toast.error('Compartilhamento de arquivo não disponível neste navegador. Baixe o PDF.')
        return
      }
      if (action === 'download') {
        const pdf = await loadPdf()
        downloadPdfFile(pdf.bytes, pdf.filename)
        return
      }
      if (action === 'copy' || action === 'whatsapp') {
        const text = await loadClinicalDocumentShareText(document.documentId, label)
        if (action === 'copy') {
          if (!navigator.clipboard?.writeText) throw new Error('Não foi possível copiar o link neste navegador.')
          await navigator.clipboard.writeText(text)
          toast.success('Link copiado')
        } else {
          openExternalUrl(buildWhatsAppUrl({ phoneE164: document.phoneE164, text }))
        }
        return
      }
      const pdf = await loadPdf()
      let text = label
      try {
        text = await loadClinicalDocumentShareText(document.documentId, label)
      } catch {
        toast.error('Não foi possível obter o link. O PDF continua disponível para envio.')
      }
      if (desktop) {
        downloadPdfFile(pdf.bytes, pdf.filename)
        openExternalUrl(buildWhatsAppUrl({ phoneE164: document.phoneE164, text }))
      } else {
        const file = pdfBytesToFile(pdf.bytes, pdf.filename)
        preparedPdf.current = { file, text, signed: document.signed, documentId: document.documentId }
        setPdfReady(true)
        if (navigator.userActivation?.isActive === false) return
        const result = await sharePdfFile(file, label, text)
        if (result === 'unavailable') {
          toast.error('Compartilhamento de arquivo não disponível neste navegador. Baixe o PDF.')
        }
      }
    } catch (error) {
      failed = true
      toast.error(toFriendlyErrorMessage(error, 'Não foi possível compartilhar o documento.'))
    } finally {
      if (!failed) trackProductEvent('document_shared', { channel: SHARE_CHANNEL[action], signed: document.signed })
      busyRef.current = false
      setBusy(null)
    }
  }

  const options = [
    { action: 'pdf', title: 'WhatsApp', detail: 'PDF', icon: MessageCircleIcon },
    { action: 'whatsapp', title: 'WhatsApp', detail: 'link', icon: LinkIcon },
    { action: 'copy', title: 'Copiar link', detail: 'PDF e validação', icon: CopyIcon },
    { action: 'download', title: 'Baixar PDF', detail: 'ou imprimir', icon: DownloadIcon },
  ] as const

  return (
    <div className="grid grid-cols-2 gap-2" aria-busy={busy !== null}>
      {options.map(({ action, title, detail, icon: Icon }) => (
        <Button
          key={action}
          variant="ghost"
          className="flex min-h-20 items-center gap-3 px-3 py-3 text-left"
          aria-label={`${title} · ${detail}`}
          disabled={busy !== null}
          onClick={() => void run(action)}
        >
          <Icon size={20} aria-hidden="true" className="shrink-0 text-[var(--color-teal-ink)]" />
          <span className="min-w-0">
            <span className="block">{title}</span>
            <span className="block text-xs font-normal text-[var(--color-text-muted)]">
              {busy === action ? 'Preparando…' : action === 'pdf' && pdfReady ? 'PDF pronto · toque para enviar' : detail}
            </span>
          </span>
        </Button>
      ))}
    </div>
  )
}
